import React, { useEffect, useRef, useMemo } from "react";
import { cn } from "@/lib/utils";

// Risk-score gauge: a damped needle sweeps across Low / Moderate / High bands
// while a risk assessment computes. Palette-mapped, reduced-motion safe.
const INK = "#20242A", OK = "#4F7A62", GOLD = "#9C6F2A", BAD = "#A85353";
const cx = 200, cy = 205;
const pt = (v, r) => { const a = Math.PI + (v * Math.PI) / 100; return [cx + Math.cos(a) * r, cy + Math.sin(a) * r]; };
const arc = (v0, v1, r) => { const a = pt(v0, r), b = pt(v1, r); return `M${a[0]} ${a[1]}A${r} ${r} 0 0 1 ${b[0]} ${b[1]}`; };

export function RiskGaugeMark({ className }) {
  const needleRef = useRef(null), readRef = useRef(null);
  const ticks = useMemo(() => {
    const t = [];
    for (let v = 0; v <= 100; v += 10) { const a = pt(v, 126), b = pt(v, v % 50 === 0 ? 112 : 119); t.push({ a, b, major: v % 50 === 0 }); }
    return t;
  }, []);
  const labels = useMemo(() => [0, 50, 100].map((v) => { const p = pt(v, 172); return { x: p[0], y: p[1] + 4, v }; }), []);

  useEffect(() => {
    const needle = needleRef.current, read = readRef.current;
    if (!needle || !read) return;
    const reduce = window.matchMedia("(prefers-reduced-motion:reduce)").matches;
    const render = (v) => {
      const vv = Math.max(0, Math.min(100, v));
      needle.setAttribute("transform", `translate(${cx} ${cy}) rotate(${vv * 1.8})`);
      const band = vv < 34 ? "Low" : vv < 67 ? "Moderate" : "High";
      read.textContent = Math.round(vv) + "  " + band;
      read.setAttribute("fill", vv < 34 ? OK : vv < 67 ? GOLD : BAD);
    };
    if (reduce) { render(71); return; }
    const tg = [22, 48, 71, 35, 88, 60, 15];
    let pos = tg[0], vel = 0, ti = 0, sw = 0, prev = 0, raf;
    const f = (ms) => {
      const dt = Math.min(0.033, (ms - prev) / 1000 || 0.016); prev = ms;
      sw += dt; if (sw > 3) { sw = 0; ti = (ti + 1) % tg.length; }
      vel += ((tg[ti] - pos) * 70 - vel * 6) * dt; pos += vel * dt;
      render(pos); raf = requestAnimationFrame(f);
    };
    raf = requestAnimationFrame(f);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className={cn(className)} data-testid="risk-gauge-mark">
      <svg viewBox="0 0 400 250" className="mx-auto h-auto w-full max-w-[360px]">
        {[[0, 33, OK], [34, 66, GOLD], [67, 100, BAD]].map(([a, b, c], i) => (
          <path key={i} d={arc(a, b, 150)} fill="none" stroke={c} strokeWidth={16} opacity={0.8} />
        ))}
        {ticks.map((t, i) => (
          <line key={i} x1={t.a[0]} y1={t.a[1]} x2={t.b[0]} y2={t.b[1]} stroke={INK} strokeWidth={t.major ? 1 : 0.6} />
        ))}
        {labels.map((l, i) => (
          <text key={i} x={l.x} y={l.y} textAnchor="middle" fill={INK} fontFamily="Georgia,serif" fontSize="12">{l.v}</text>
        ))}
        <text x="200" y="26" textAnchor="middle" fill={INK} fontFamily="Georgia,serif" fontSize="13">Risk score</text>
        <g ref={needleRef} transform={`translate(${cx} ${cy}) rotate(${71 * 1.8})`}>
          <line x1="0" y1="0" x2="-118" y2="0" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
        </g>
        <circle cx={cx} cy={cy} r="8" fill={INK} />
        <text ref={readRef} x="200" y="240" textAnchor="middle" fontSize="20" fontFamily="Georgia,serif" fill={BAD}>71  High</text>
      </svg>
    </div>
  );
}
